import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Play, Download, Calendar, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { useCampusAuth } from "@/contexts/CampusAuth";
import BriefingFormDialog from "@/components/campus/BriefingFormDialog";
import { downloadBriefingIcs, isUpcoming } from "@/lib/briefingUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function BriefingRow({ b, isAdmin, onEdit, onDelete }) {
  const upcoming = isUpcoming(b);
  return (
    <li data-testid={`briefing-${b.id}`} className="py-7 lg:py-8 grid lg:grid-cols-[1fr_auto] gap-6 items-start">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-sand-500 mb-2">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" strokeWidth={1.6} />
            {fmtDate(b.date)}{upcoming && b.time_label ? ` · ${b.time_label}` : ""}
          </span>
          <span>·</span>
          <span>{b.duration}</span>
          <span>·</span>
          <span>{b.speaker}</span>
          {upcoming && (
            <span className="ml-2 inline-flex items-center rounded-full bg-terra/10 text-terra px-2.5 py-0.5 text-[10.5px] uppercase tracking-[0.15em] font-bold">
              Bevorstehend
            </span>
          )}
        </div>
        <h3 className="font-display text-sand-800 text-[1.3rem] lg:text-[1.45rem] font-semibold leading-snug">
          {b.title}
        </h3>
        {b.highlights?.length > 0 && (
          <ul className="mt-3 space-y-1 text-[13.5px] text-sand-600 leading-relaxed">
            {b.highlights.map((h) => (
              <li key={h} className="flex items-start gap-2">
                <span className="mt-2 inline-block h-1 w-1 rounded-full bg-sand-400 shrink-0" />
                {h}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-2 lg:items-end">
        {upcoming ? (
          <button
            onClick={() => downloadBriefingIcs(b)}
            className="inline-flex items-center gap-2 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-10 px-5 text-[13px] font-medium"
            data-testid={`briefing-cta-${b.id}`}
          >
            Im Kalender vormerken
          </button>
        ) : (
          <>
            {b.recording_url ? (
              <a href={b.recording_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-cream-50 border border-cream-300 hover:bg-cream-100 text-sand-800 h-10 px-5 text-[13px] font-medium"
                data-testid={`briefing-play-${b.id}`}>
                <Play className="h-3.5 w-3.5" strokeWidth={1.6} />
                Aufzeichnung ansehen
              </a>
            ) : (
              <span className="text-[12px] text-sand-500 italic">Aufzeichnung folgt in Kürze</span>
            )}
            {b.slides_url && (
              <a href={b.slides_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 text-[12px] text-sand-600 hover:text-sand-800 px-2"
                data-testid={`briefing-slides-${b.id}`}>
                <Download className="h-3.5 w-3.5" strokeWidth={1.6} />
                Slides als PDF
              </a>
            )}
          </>
        )}

        {isAdmin && (
          <div className="flex gap-2 mt-1">
            <button onClick={() => onEdit(b)} data-testid={`briefing-edit-${b.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-cream-300 hover:bg-cream-100 text-sand-600 h-8 px-3 text-[11.5px] font-medium">
              <Pencil className="h-3 w-3" strokeWidth={1.8} /> Bearbeiten
            </button>
            <button onClick={() => onDelete(b)} data-testid={`briefing-delete-${b.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-cream-300 hover:bg-terra/10 hover:text-terra text-sand-600 h-8 px-3 text-[11.5px] font-medium">
              <Trash2 className="h-3 w-3" strokeWidth={1.8} /> Löschen
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

export default function CampusBriefings() {
  const { user } = useCampusAuth();
  const [briefings, setBriefings] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/briefings`, { withCredentials: true })
      .then(({ data }) => setBriefings(data))
      .catch(() => toast.error("Briefings konnten nicht geladen werden."));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (b) => {
    if (!window.confirm(`„${b.title}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/briefings/${b.id}`, { withCredentials: true });
      toast.success("Briefing gelöscht.");
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const upcoming = (briefings || []).filter(isUpcoming).sort((a, b) => a.date.localeCompare(b.date));
  const archive = (briefings || []).filter((b) => !isUpcoming(b));

  return (
    <div className="space-y-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[12px] uppercase tracking-[0.28em] font-medium text-sand-500 mb-3">
            Helden-Briefings  ·  Live & Archiv
          </div>
          <h1 className="font-display text-sand-800 text-3xl sm:text-[2.4rem] font-semibold leading-[1.1]">
            Live-Briefings  &  Archiv.
          </h1>
          <p className="mt-4 text-sand-600 text-[15.5px] leading-[1.65] max-w-2xl">
            Jeden Monat 60 Minuten konkrete Antworten auf neue Gesetzeslagen, Reformen und
            Praxisfragen – live mit Dickie & wechselnden Expert:innen.
          </p>
        </div>
        {user?.is_admin && (
          <button
            onClick={() => { setEditing(null); setFormOpen(true); }}
            data-testid="briefing-add-btn"
            className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white h-11 px-5 text-[13.5px] font-semibold"
          >
            <Plus className="h-4 w-4" strokeWidth={2} /> Neues Briefing
          </button>
        )}
      </header>

      {!briefings && (
        <div className="flex items-center gap-2 text-sand-600 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Lade Briefings …
        </div>
      )}

      {briefings && (
        <>
          {/* Bevorstehend */}
          <section>
            <h2 className="text-[12px] uppercase tracking-[0.24em] font-bold text-terra mb-4">Bevorstehend</h2>
            {upcoming.length === 0 ? (
              <div data-testid="briefings-empty-upcoming" className="rounded-2xl border border-cream-300 bg-cream-50 p-7 text-sand-600 text-[14px]">
                Der nächste Live-Termin wird gerade geplant – schau bald wieder vorbei.
              </div>
            ) : (
              <ul className="divide-y divide-cream-300 border-y border-cream-300">
                {upcoming.map((b) => (
                  <BriefingRow key={b.id} b={b} isAdmin={user?.is_admin}
                    onEdit={(x) => { setEditing(x); setFormOpen(true); }} onDelete={handleDelete} />
                ))}
              </ul>
            )}
          </section>

          {/* Archiv */}
          {archive.length > 0 && (
            <section>
              <h2 className="text-[12px] uppercase tracking-[0.24em] font-bold text-sand-500 mb-4">Archiv</h2>
              <ul className="divide-y divide-cream-300 border-y border-cream-300">
                {archive.map((b) => (
                  <BriefingRow key={b.id} b={b} isAdmin={user?.is_admin}
                    onEdit={(x) => { setEditing(x); setFormOpen(true); }} onDelete={handleDelete} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <BriefingFormDialog briefing={editing} open={formOpen} onOpenChange={setFormOpen} onSaved={load} />
    </div>
  );
}
